import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './ProjectsList.css';
import ProjectCard from './ProjectCard/ProjectCard';
import { useProject } from '../../../contexts/ProjectContext';
import { useEC2InstanceStatus } from '../../../contexts/EC2InstanceStatusContext';
import RefreshButton from '../../RefreshButton/RefreshButton';
import useCachedApi from '../../../hooks/useCachedApi';

const ProjectsList = ({ projects = [], loading, onProjectSelect, onViewProject, onRefresh, user, customRedirectPaths = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'active'
  const [sortBy, setSortBy] = useState('Modified'); // 'Name', 'Modified', 'ModelModified', 'LastViewed', 'UserRole', 'Size'
  const [sortOrder, setSortOrder] = useState('DESC'); // 'ASC', 'DESC'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fetchProjects, setExistingProjectName] = useState("");
  
  // Use project context utilities
  const { formatDate, formatFileSize, isProjectExpired, canViewProject, canAccessProjectFiles } = useProject();
  
  // Use EC2 instance status context for polling
  const { startPolling, stopPolling, updateProjects } = useEC2InstanceStatus();
  
  // Memoize startPolling and stopPolling to prevent infinite loops
  const startPollingRef = useRef(startPolling);
  const stopPollingRef = useRef(stopPolling);
  
  useEffect(() => {
    startPollingRef.current = startPolling;
    stopPollingRef.current = stopPolling;
  }, [startPolling, stopPolling]);

  // Debounce search term with shorter delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 50); // Reduced to 50ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized filtering and sorting for better performance
  const filteredProjects = useMemo(() => {
    if (!projects || !Array.isArray(projects)) {
      return [];
    }
    
    let filtered = [...projects];

    // Apply search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.Name.toLowerCase().includes(searchLower) ||
        project.Plan.toLowerCase().includes(searchLower) ||
        project.UserRole.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filterType === 'active') {
      const now = new Date();
      filtered = filtered.filter(project => new Date(project.Expiration) > now);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle date fields
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'ASC') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [projects, debouncedSearchTerm, filterType, sortBy, sortOrder]);
  
  // Start/stop polling when projects change
  useEffect(() => {
    if (projects && projects.length > 0) {
      const projectIds = projects.map(p => String(p.Id));
      startPollingRef.current(projectIds);
    } else {
      stopPollingRef.current();
    }
    
    // Cleanup on unmount
    return () => {
      stopPollingRef.current();
    };
  }, [projects]); // Removed startPolling and stopPolling from dependencies to prevent infinite loop
  
  // Note: We don't need to update projects on filteredProjects change
  // The main projects list is already being polled via the projects useEffect above
  // Filtering/sorting is just a view concern, not a polling concern

  const handleSortChange = useCallback((value) => {
    const [field, label, order] = value.split('|');
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };


  if (loading) {
    return (
      <div className="projects-list-loading">
        <div className="projects-list-loading-spinner"></div>
        Loading projects...
      </div>
    );
  }

  return (
    <div className="projects-list-container">
      {/* Header Controls */}
      <div className="projects-header">
        <div className="projects-controls">
          {/* Search */}
          <div className="search-container">
            <input
              type="search"
              placeholder="Search Projects"
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <i className="fas fa-search search-icon"></i>
          </div>

          {/* Filters */}
          <div className="filter-controls">
            <div className="filter-group">
              <label>Show</label>
              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
                className="filter-select"
              >
                <option value="all">Show All</option>
                <option value="active">Show Active</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sort by</label>
              <select 
                onChange={(e) => handleSortChange(e.target.value)}
                className="filter-select"
                defaultValue="Modified|Last update|DESC"
              >
                <option value="Name|Project Name|ASC">Project Name</option>
                <option value="Modified|Last update|DESC">Last update</option>
                <option value="ModelModified|Last model update|DESC">Last model update</option>
                <option value="LastViewed|Last Viewed|DESC">Last Viewed</option>
                <option value="UserRole|Role|ASC">Role</option>
                <option value="Size|Project Size|DESC">Project Size</option>
              </select>
            </div>
          </div>



          {/* View Mode Toggle */}
          <div className="view-controls">
            <RefreshButton
              onRefresh={handleRefresh}
              loading={isRefreshing}
              size="medium"
              variant="secondary"
              showText={false}
              className="projects-refresh-btn"
            />
          </div>
        </div>
      </div>

      {/* Projects Grid/List */}
      <div className="projects-content gallery">
        {isRefreshing ? (
          <div className="projects-list-loading">
            <div className="loading-spinner">
            </div>
            <p>Refreshing projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="no-projects">
            <i className="fas fa-folder-open"></i>
            <h3>No projects found</h3>
            <p>
              {debouncedSearchTerm || filterType !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'No projects available at the moment.'}
            </p>
          </div>
        ) : (
          <div className="projects-grid gallery">
            {filteredProjects.map(project => (
              <ProjectCard
                key={project.Id}
                project={project}
                onProjectSelect={onProjectSelect}
                onViewProject={onViewProject}
                formatDate={formatDate}
                user={user}
                customRedirectPath={customRedirectPaths[project.Id]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;
